import { z } from 'zod';
import { blockRowSchema, instantSchema, uuidSchema } from '../schema/rows.js';
import { DomainError, isActionableKind } from './types.js';
/**
 * Completing and reopening from a list view (docs/04 §3 rules 5 and 7, docs/04 §4 rule 7,
 * design D3 of f1-asap-view): one transaction that can also complete the open subtasks and mark
 * the TAI as achieved, returned with the operations that undo it («Deshacer» for 5 s).
 * `completeBlock` / `reopenBlock` stay as the single-row commands of F0.
 */
const taskState = z.object({
    id: uuidSchema,
    kind: blockRowSchema.shape.kind,
    status: blockRowSchema.shape.status,
    completed_at: blockRowSchema.shape.completed_at,
});
const highlightState = z.object({ id: uuidSchema, achieved_at: instantSchema.nullable() });
const completeTaskInput = z.object({
    block: taskState,
    /** Children of the block (not deleted); the open ones are completed with `withChildren`. */
    children: z.array(taskState).default([]),
    withChildren: z.boolean().default(false),
    /** The block's active highlight in the current period, if any (TAI). */
    highlight: highlightState.nullable().default(null),
    now: instantSchema.optional(),
});
const patch = (table, id, data) => ({ table, op: 'PATCH', id: id, data });
function assertActionable(block) {
    if (!isActionableKind(block.kind) || block.status === null) {
        throw new DomainError('invariant_violation', `Solo los bloques accionables se pueden completar; ${block.kind} no lo es.`, 'INV-03');
    }
}
/** Completes a task (INV-06), optionally with its open subtasks, and achieves its TAI. */
export function completeTask(input) {
    const p = completeTaskInput.parse(input);
    assertActionable(p.block);
    if (p.block.status === 'done')
        return { operations: [], undo: [] };
    const now = p.now ?? new Date().toISOString();
    const targets = [
        p.block,
        ...(p.withChildren
            ? p.children.filter((child) => child.status === 'open' && isActionableKind(child.kind))
            : []),
    ];
    const operations = targets.map((t) => patch('blocks', t.id, { status: 'done', completed_at: now }));
    const undo = targets.map((t) => patch('blocks', t.id, { status: t.status, completed_at: t.completed_at }));
    if (p.highlight?.achieved_at === null) {
        operations.push(patch('highlights', p.highlight.id, { achieved_at: now }));
        undo.unshift(patch('highlights', p.highlight.id, { achieved_at: null }));
    }
    return { operations, undo };
}
const reopenTaskInput = completeTaskInput.pick({ block: true, highlight: true });
/** Reopens a done task (INV-06) and takes back its TAI's achievement (docs/04 §4 rule 7). */
export function reopenTask(input) {
    const p = reopenTaskInput.parse(input);
    assertActionable(p.block);
    if (p.block.status !== 'done')
        return { operations: [], undo: [] };
    const operations = [
        patch('blocks', p.block.id, { status: 'open', completed_at: null }),
    ];
    const undo = [
        patch('blocks', p.block.id, { status: 'done', completed_at: p.block.completed_at }),
    ];
    if (p.highlight?.achieved_at) {
        operations.push(patch('highlights', p.highlight.id, { achieved_at: null }));
        undo.unshift(patch('highlights', p.highlight.id, { achieved_at: p.highlight.achieved_at }));
    }
    return { operations, undo };
}
//# sourceMappingURL=complete.js.map